import type { Route } from '@angular/router';

type BreadcrumbIcon = 'robotStatus' | undefined;
export class Breadcrumb {
  displayName: string;
  url: string;
  terminal: boolean;
  route: Route | null;
  icon?: BreadcrumbIcon;

  constructor(displayName: string, url: string, terminal: boolean = false, route?: Route, icon?: BreadcrumbIcon) {
    this.displayName = displayName;
    this.url = url;
    this.terminal = terminal;
    this.route = route;
    this.icon = icon;
  }
}

export enum BreadcrumbPageType {
  // Level 1
  STREAM_LIST,
  ROBOTS_LIST,
  OPERATIONS_LIST,
  MAPS_LIST,
  ENVIRONMENT,
  FUNCTIONS_LIST,
  STORAGE_EXPLORER,
  ROBOT_DEFS_LIST,
  AUTOMATE_LIST,
  AUTOMATE_INFO,
  MAP_VIEWER,

  // Level 2
  STREAM_INFO,
  ROBOT_INFO,
  OPERATION_INFO,
  FUNCTION_INFO,
  ROBOT_DEF_INFO,
  PROJECT_SETTINGS,
}

export class BreadcrumbHelper {
  static robotInfoRegex = /\/project\/.+\/robots\/.+\/.+/;
  static automateListRegex = /(\/project\/.+\/automate)$/;
  static automateInfoRegex = /\/project\/.+\/automate\/.+/;
  static streamInfoRegex = /\/project\/.+\/streams\/.+/;
  static operationInfoRegex = /\/project\/.+\/operations\/(local|global|custom)\/.+/;
  static functionInfoRegex = /\/project\/.+\/functions\/.+\/.+/;
  static robotDefInfoRegex = /\/project\/.+\/robot-profiles\/.+\/.+/;
  static streamListRegex = /(\/project\/.+\/streams)/;
  static robotsListRegex = /(\/project\/.+\/robots)/;
  static operationsListRegex = /(\/project\/.+\/operations)\/list/;
  static mapsListRegex = /(\/project\/[^/]+\/maps)\/list/;
  static mapViewerRegex = /\/project\/[^/]+\/maps\/view\/[^/]+/;
  static environmentRegex = /(\/project\/[^/]+)\/environment$/;
  static functionsListRegex = /(\/project\/.+\/functions)\/list/;
  static storageExplorerRegex = /\/project\/.+\/analyze\/data-explorer/;
  static robotDefsListRegex = /(\/project\/.+\/robot-profiles)\/list/;
  static projectSettingsRegex = /(\/project\/.+)\/settings\/.+/;

  private static operationInfoListPartRegex = /(\/project\/.+\/operations)/;
  private static functionInfoListPartRegex = /(\/project\/.+\/functions)/;
  private static automateInfoPartRegex = /(\/project\/.+\/automate)\/([^/\n\r]+)/;

  private static robotDefInfoListPartRegex = /(\/project\/.+\/robot-profiles)/;
  private static projectPartRegex = /(\/project\/.+)\/settings/;
  private static projectSettingsPartRegex = /(\/project\/.+\/settings)/;
  private static projectIdPartRegex = /(\/project\/[^/]+)/;

  private static readonly ROBOT_MAPS_LABEL = 'Robot Maps';

  static getBreadcrumbByUrl(url: string) {
    const type = this.getTypeByUrl(url);

    return this.getBreadcrumb(type, url);
  }

  static getTypeByUrl(url: string) {
    if (this.robotInfoRegex.test(url)) {
      return BreadcrumbPageType.ROBOT_INFO;
    } else if (this.streamInfoRegex.test(url)) {
      return BreadcrumbPageType.STREAM_INFO;
    } else if (this.operationInfoRegex.test(url)) {
      return BreadcrumbPageType.OPERATION_INFO;
    } else if (this.functionInfoRegex.test(url)) {
      return BreadcrumbPageType.FUNCTION_INFO;
    } else if (this.robotDefInfoRegex.test(url)) {
      return BreadcrumbPageType.ROBOT_DEF_INFO;
    } else if (this.streamListRegex.test(url)) {
      return BreadcrumbPageType.STREAM_LIST;
    } else if (this.robotsListRegex.test(url)) {
      return BreadcrumbPageType.ROBOTS_LIST;
    } else if (this.operationsListRegex.test(url)) {
      return BreadcrumbPageType.OPERATIONS_LIST;
    } else if (this.mapViewerRegex.test(url)) {
      return BreadcrumbPageType.MAP_VIEWER;
    } else if (this.mapsListRegex.test(url)) {
      return BreadcrumbPageType.MAPS_LIST;
    } else if (this.environmentRegex.test(url)) {
      return BreadcrumbPageType.ENVIRONMENT;
    } else if (this.functionsListRegex.test(url)) {
      return BreadcrumbPageType.FUNCTIONS_LIST;
    } else if (this.storageExplorerRegex.test(url)) {
      return BreadcrumbPageType.STORAGE_EXPLORER;
    } else if (this.robotDefsListRegex.test(url)) {
      return BreadcrumbPageType.ROBOT_DEFS_LIST;
    } else if (this.projectSettingsRegex.test(url)) {
      return BreadcrumbPageType.PROJECT_SETTINGS;
    }
    return this.getAutomateType(url);
  }

  static getTargetUrl(type: BreadcrumbPageType, url: string, level: number = 0): string {
    switch (type) {
      case BreadcrumbPageType.STREAM_LIST:
      case BreadcrumbPageType.ROBOTS_LIST:
      case BreadcrumbPageType.OPERATIONS_LIST:
      case BreadcrumbPageType.MAPS_LIST:
      case BreadcrumbPageType.ENVIRONMENT:
      case BreadcrumbPageType.FUNCTIONS_LIST:
      case BreadcrumbPageType.STORAGE_EXPLORER:
      case BreadcrumbPageType.ROBOT_DEFS_LIST:
      case BreadcrumbPageType.AUTOMATE_LIST:
        return url;
      case BreadcrumbPageType.STREAM_INFO:
        return this.resolveByLevel(url, level, this.streamListRegex);
      case BreadcrumbPageType.ROBOT_INFO:
        return this.resolveByLevel(url, level, this.robotsListRegex);
      case BreadcrumbPageType.OPERATION_INFO:
        return this.resolveByLevel(url, level, this.operationInfoListPartRegex);
      case BreadcrumbPageType.FUNCTION_INFO:
        return this.resolveByLevel(url, level, this.functionInfoListPartRegex);
      case BreadcrumbPageType.ROBOT_DEF_INFO:
        return this.resolveByLevel(url, level, this.robotDefInfoListPartRegex);
      case BreadcrumbPageType.AUTOMATE_INFO: {
        const match = url.match(this.automateInfoPartRegex);
        return level === 1 ? match?.[2] ?? url : match?.[1] ?? '';
      }
      case BreadcrumbPageType.PROJECT_SETTINGS:
        return level === 1
          ? url.match(this.projectSettingsPartRegex)?.[0] ?? ''
          : url.match(this.projectPartRegex)?.[1] ?? '';
      case BreadcrumbPageType.MAP_VIEWER: {
        if (level === 1) return url;
        const match = url.match(this.projectIdPartRegex);
        return match?.[1] ? `${match[1]}/maps/list` : url;
      }
      default:
        return '';
    }
  }

  static getBreadcrumb(type: BreadcrumbPageType, url: string) {
    const breadcrumbs: Breadcrumb[] = [];
    let bc: Breadcrumb;

    switch (type) {
      case BreadcrumbPageType.STREAM_LIST:
        bc = new Breadcrumb('Storage Streams', this.getTargetUrl(type, url), true);
        breadcrumbs.push(bc);
        break;
      case BreadcrumbPageType.STREAM_INFO:
        bc = new Breadcrumb('Storage Streams', this.getTargetUrl(type, url, 0), false);
        breadcrumbs.push(bc);
        bc = new Breadcrumb('', this.getTargetUrl(type, url, 1), true);
        breadcrumbs.push(bc);
        break;

      case BreadcrumbPageType.ROBOTS_LIST:
        bc = new Breadcrumb('Robots', this.getTargetUrl(type, url), true);
        breadcrumbs.push(bc);
        break;
      case BreadcrumbPageType.ROBOT_INFO:
        bc = new Breadcrumb('Robots', this.getTargetUrl(type, url, 0), false);
        breadcrumbs.push(bc);
        bc = new Breadcrumb('', this.getTargetUrl(type, url, 1), true, null, 'robotStatus');
        breadcrumbs.push(bc);
        break;

      case BreadcrumbPageType.OPERATIONS_LIST:
        bc = new Breadcrumb('Operations', this.getTargetUrl(type, url), true);
        breadcrumbs.push(bc);
        break;
      case BreadcrumbPageType.OPERATION_INFO:
        bc = new Breadcrumb('Operations', this.getTargetUrl(type, url, 0), false);
        breadcrumbs.push(bc);
        bc = new Breadcrumb('', this.getTargetUrl(type, url, 1), true);
        breadcrumbs.push(bc);
        break;

      case BreadcrumbPageType.STORAGE_EXPLORER:
        bc = new Breadcrumb('Storage Explorer', this.getTargetUrl(type, url), true);
        breadcrumbs.push(bc);
        break;

      case BreadcrumbPageType.ROBOT_DEFS_LIST:
        bc = new Breadcrumb('Robot Profiles', this.getTargetUrl(type, url), true);
        breadcrumbs.push(bc);
        break;
      case BreadcrumbPageType.ROBOT_DEF_INFO:
        bc = new Breadcrumb('Robot Profiles', this.getTargetUrl(type, url, 0), false);
        breadcrumbs.push(bc);
        bc = new Breadcrumb('', this.getTargetUrl(type, url, 1), true);
        breadcrumbs.push(bc);
        break;

      case BreadcrumbPageType.AUTOMATE_LIST:
        bc = new Breadcrumb('Automate', this.getTargetUrl(type, url), true);
        breadcrumbs.push(bc);
        break;
      case BreadcrumbPageType.AUTOMATE_INFO:
        bc = new Breadcrumb('Automate', this.getTargetUrl(type, url, 0), false);
        breadcrumbs.push(bc);
        bc = new Breadcrumb(this.getTargetUrl(type, url, 1), url, true);
        breadcrumbs.push(bc);
        break;

      case BreadcrumbPageType.PROJECT_SETTINGS:
        bc = new Breadcrumb('Project', this.getTargetUrl(type, url, 0), false);
        breadcrumbs.push(bc);
        bc = new Breadcrumb('Settings', this.getTargetUrl(type, url, 1), true);
        breadcrumbs.push(bc);
        break;
      case BreadcrumbPageType.MAPS_LIST:
        bc = new Breadcrumb(this.ROBOT_MAPS_LABEL, this.getTargetUrl(type, url), true);
        breadcrumbs.push(bc);
        break;
      case BreadcrumbPageType.MAP_VIEWER:
        bc = new Breadcrumb(this.ROBOT_MAPS_LABEL, this.getTargetUrl(type, url, 0), false);
        breadcrumbs.push(bc);
        bc = new Breadcrumb('', this.getTargetUrl(type, url, 1), true);
        breadcrumbs.push(bc);
        break;
      case BreadcrumbPageType.ENVIRONMENT: {
        const match = url.match(this.projectIdPartRegex);
        const mapsUrl = match?.[1] ? `${match[1]}/maps/list` : url;
        bc = new Breadcrumb(this.ROBOT_MAPS_LABEL, mapsUrl, false);
        breadcrumbs.push(bc);
        bc = new Breadcrumb('Environment', url, true);
        breadcrumbs.push(bc);
        break;
      }
      default:
        break;
    }

    return breadcrumbs;
  }

  private static getAutomateType(url: string): BreadcrumbPageType | undefined {
    if (this.automateInfoRegex.test(url)) return BreadcrumbPageType.AUTOMATE_INFO;
    if (this.automateListRegex.test(url)) return BreadcrumbPageType.AUTOMATE_LIST;
    return undefined;
  }

  private static resolveByLevel(url: string, level: number, regex: RegExp): string {
    if (level === 1) return url;
    return url.match(regex)?.[0] ?? '';
  }
}
